/**
 * Aho-Corasick multi-phrase matcher (ENGINE-SPEC §5, §11.6): one automaton, linear in the text.
 *
 * It reproduces, per phrase, the seed's `new RegExp('\\b' + escRe(p).replace(/\s+/g, '\\s+') +
 * '\\b', 'gi')` hit set:
 *   - case-insensitive by ASCII fold only (the `i` flag without `u`),
 *   - any run of JS `\s` in the text matches a space in the phrase,
 *   - `\b` is ASCII-word based, checked on the original characters around the match,
 *   - hits of one phrase never overlap (regex `g` semantics), hits of different phrases may.
 *
 * The automaton runs over the folded text itself rather than token ids, so intra-phrase
 * punctuation (`it's`, `game-changer`, U+2019) and the seed's `\b` semantics are preserved
 * exactly. Offsets are into the string given, plus `offsetBase`.
 */

import { IS_SPACE, foldAscii, isWordCode } from './chars.ts';

export type PhraseEmit = (start: number, end: number, phraseIndex: number) => void;

export class PhraseMatcher {
	readonly size: number;
	private readonly alpha: Uint16Array;
	private readonly classes: number;
	private readonly spaceClass: number;
	private readonly next: Int32Array;
	private readonly outStart: Int32Array;
	private readonly outList: Int32Array;
	private readonly symLen: Uint16Array;
	private readonly startWord: Uint8Array;
	private readonly endWord: Uint8Array;
	private readonly ringMask: number;
	private readonly lastEnd: Int32Array;
	private readonly ring: Int32Array;

	constructor(phrases: readonly string[]) {
		this.size = phrases.length;
		const seqs: number[][] = [];
		const alphabet = new Map<number, number>();
		let maxLen = 1;
		const startWord = new Uint8Array(phrases.length);
		const endWord = new Uint8Array(phrases.length);
		phrases.forEach((p, idx) => {
			const seq: number[] = [];
			let inSpace = false;
			for (let i = 0; i < p.length; i++) {
				const c = p.charCodeAt(i);
				if (IS_SPACE[c]) {
					if (!inSpace) seq.push(32);
					inSpace = true;
				} else {
					inSpace = false;
					seq.push(foldAscii(c));
				}
			}
			if (seq.length === 0 || seq[0] === 32 || seq[seq.length - 1] === 32) {
				throw new Error(`PhraseMatcher: phrase ${idx} is empty or has edge whitespace`);
			}
			startWord[idx] = isWordCode(seq[0] as number) ? 1 : 0;
			endWord[idx] = isWordCode(seq[seq.length - 1] as number) ? 1 : 0;
			for (const c of seq) if (!alphabet.has(c)) alphabet.set(c, alphabet.size + 1);
			if (seq.length > maxLen) maxLen = seq.length;
			seqs.push(seq);
		});
		this.startWord = startWord;
		this.endWord = endWord;
		this.classes = alphabet.size + 1;
		this.alpha = new Uint16Array(65536);
		for (const [c, id] of alphabet) this.alpha[c] = id;
		// Whitespace in the text maps to the class of the space symbol (0 = "other" if unused).
		this.spaceClass = alphabet.get(32) ?? 0;
		let ring = 1;
		while (ring <= maxLen) ring <<= 1;
		this.ringMask = ring - 1;
		this.ring = new Int32Array(ring);
		this.lastEnd = new Int32Array(phrases.length);
		this.symLen = Uint16Array.from(seqs.map((s) => s.length));

		// Trie.
		const nc = this.classes;
		let go: number[] = new Array(nc).fill(-1);
		const own: number[][] = [[]];
		let states = 1;
		seqs.forEach((seq, pi) => {
			let s = 0;
			for (const c of seq) {
				const cls = alphabet.get(c) as number;
				let t = go[s * nc + cls] as number;
				if (t < 0) {
					t = states++;
					go[s * nc + cls] = t;
					for (let k = 0; k < nc; k++) go.push(-1);
					own.push([]);
				}
				s = t;
			}
			(own[s] as number[]).push(pi);
		});
		// BFS: failure links, full transition table, flattened outputs.
		const fail = new Int32Array(states);
		const order: number[] = [];
		for (let c = 0; c < nc; c++) {
			const t = go[c] as number;
			if (t < 0) go[c] = 0;
			else {
				fail[t] = 0;
				order.push(t);
			}
		}
		for (let qi = 0; qi < order.length; qi++) {
			const s = order[qi] as number;
			const f = fail[s] as number;
			(own[s] as number[]).push(...(own[f] as number[]));
			for (let c = 0; c < nc; c++) {
				const t = go[s * nc + c] as number;
				const viaFail = go[f * nc + c] as number;
				if (t < 0) go[s * nc + c] = viaFail;
				else {
					fail[t] = viaFail;
					order.push(t);
				}
			}
		}
		this.next = Int32Array.from(go);
		go = [];
		const outStart = new Int32Array(states + 1);
		const flat: number[] = [];
		for (let s = 0; s < states; s++) {
			outStart[s] = flat.length;
			flat.push(...[...new Set(own[s] as number[])].sort((a, b) => a - b));
		}
		outStart[states] = flat.length;
		this.outStart = outStart;
		this.outList = Int32Array.from(flat);
	}

	/**
	 * Emit every hit as (offsetBase + start, offsetBase + end, phraseIndex), in order of end
	 * offset. Returns the number of hits.
	 */
	matchAll(text: string, offsetBase: number, emit: PhraseEmit): number {
		const n = text.length;
		const { next, alpha, classes, outStart, outList, symLen, startWord, endWord } = this;
		const { ring, ringMask, lastEnd } = this;
		lastEnd.fill(-1);
		let state = 0;
		let k = 0;
		let inSpace = false;
		let hits = 0;
		for (let i = 0; i < n; i++) {
			const c = text.charCodeAt(i);
			let cls: number;
			if (IS_SPACE[c]) {
				if (inSpace) continue;
				inSpace = true;
				cls = this.spaceClass;
			} else {
				inSpace = false;
				cls = alpha[foldAscii(c)] as number;
			}
			ring[k & ringMask] = i;
			k++;
			state = next[state * classes + cls] as number;
			const o1 = outStart[state + 1] as number;
			for (let o = outStart[state] as number; o < o1; o++) {
				const p = outList[o] as number;
				const s = ring[(k - (symLen[p] as number)) & ringMask] as number;
				if (s < (lastEnd[p] as number)) continue;
				const e = i + 1;
				const prevWord = s > 0 && isWordCode(text.charCodeAt(s - 1));
				if ((startWord[p] === 1) === prevWord) continue;
				const nextWord = e < n && isWordCode(text.charCodeAt(e));
				if ((endWord[p] === 1) === nextWord) continue;
				lastEnd[p] = e;
				hits++;
				emit(offsetBase + s, offsetBase + e, p);
			}
		}
		return hits;
	}

	/** Hits per phrase. */
	counts(text: string): Uint32Array {
		const out = new Uint32Array(this.size);
		this.matchAll(text, 0, (_s, _e, p) => {
			out[p] = (out[p] as number) + 1;
		});
		return out;
	}
}
